import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('rps_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin')) {
      localStorage.removeItem('rps_admin_token');
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const propertyAPI = {
  list: async ({ city, search } = {}) => {
    const params = {};
    if (city) params.city = city;
    if (search) params.search = search;
    const { data } = await apiClient.get('/properties/', { params });
    return data;
  },
  get: async (id) => {
    const { data } = await apiClient.get(`/properties/${id}`);
    return data;
  },
  getByCode: async (code) => {
    const { data } = await apiClient.get(`/properties/search/code/${code}`);
    return data;
  },
  filterOptions: async () => {
    const { data } = await apiClient.get('/properties/filter-options');
    return data;
  },
};

export const adminAPI = {
  login: async (email, password) => {
    const { data } = await apiClient.post('/admin/login', { email, password });
    return data;
  },
  me: async () => {
    const { data } = await apiClient.get('/admin/me');
    return data;
  },
  changePassword: async (current_password, new_password) => {
    const { data } = await apiClient.put('/admin/change-password', {
      current_password,
      new_password,
    });
    return data;
  },
  stats: async () => {
    const { data } = await apiClient.get('/admin/dashboard/stats');
    return data;
  },
  listProperties: async () => {
    const { data } = await apiClient.get('/admin/properties');
    return data;
  },
  listLeads: async () => {
    const { data } = await apiClient.get('/admin/leads');
    return data;
  },
  createProperty: async (payload) => {
    const { data } = await apiClient.post('/admin/properties', payload);
    return data;
  },
  updateProperty: async (id, payload) => {
    const { data } = await apiClient.put(`/admin/properties/${id}`, payload);
    return data;
  },
  deleteProperty: async (id) => {
    const { data } = await apiClient.delete(`/admin/properties/${id}`);
    return data;
  },
  toggleLive: async (id) => {
    const { data } = await apiClient.post(`/admin/properties/${id}/toggle-live`);
    return data;
  },
  importFromAirbnb: async (airbnb_url) => {
    const { data } = await apiClient.post(
      '/admin/import-airbnb',
      { airbnb_url },
      { timeout: 120000 } // scraping + Cloudinary upload can take a while
    );
    return data;
  },
  exportLeads: async () => {
    const { data } = await apiClient.get('/admin/leads/export', { responseType: 'blob' });
    return data;
  },
};

export const guestAPI = {
  sendOtp: async (first_name, phone) => {
    const { data } = await apiClient.post('/guest/send-otp', { first_name, phone });
    return data;
  },
  verifyOtp: async (first_name, phone, code) => {
    const { data } = await apiClient.post('/guest/verify-otp', { first_name, phone, code });
    return data;
  },
  checkSession: async (token) => {
    const { data } = await apiClient.post('/guest/session', { token });
    return data;
  },
};

// Cloudinary direct upload
export const uploadImageToCloudinary = async (file) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary is not configured');
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  const res = await axios.post(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    formData
  );
  return res.data.secure_url;
};

export default apiClient;
